import React from "react";
import { Alert, Text, TextLink } from "@origin-digital/ods-core";
import { AlertTones } from "@origin-digital/ods-types/.";
import { useAppContext } from "src/context/AppContext";
import { useFormikContext } from "formik";
import { FormValues } from "./types";

export enum InternetRequestStatusEnum {
  CAN_RETAIL = "CAN_RETAIL",
  NOT_AVAILABLE = "NOT_AVAILABLE",
  PRE_INSTALL_NBN = "PRE_INSTALL_NBN",
  PRE_INSTALL_REQD_OPTICOMM = "PRE_INSTALL_REQD_OPTICOMM",
  ADDRESS_NOT_FOUND = "ADDRESS_NOT_FOUND",
}

export enum InternetRequestRawStatusEnum {
  DRAFT = "DRAFT",
  IN_PROGRESS = "IN_PROGRESS",
  CONNECTION_REQUESTED = "CONNECTION_REQUESTED",
  PROCESSING_BUT_PENDING_ON_ACCOUNT = "PROCESSING_BUT_PENDING_ON_ACCOUNT",
  REPLY_NEEDED = "REPLY_NEEDED",
  SCHEDULED = "SCHEDULED",
  COMPLETE = "COMPLETE",
  CANCELLED = "CANCELLED",
}

const INTERNET_CONN_STATUS_ALERTS: Partial<
  Record<
    InternetRequestStatusEnum,
    { message: React.ReactNode; tone: AlertTones; title: string }
  >
> = {
  [InternetRequestStatusEnum.NOT_AVAILABLE]: {
    message: (
      <Text>
        Sorry, we're unable to offer a internet connection at this address.
      </Text>
    ),
    tone: "caution",
    title: "Internet connection not available",
  },
  [InternetRequestStatusEnum.PRE_INSTALL_REQD_OPTICOMM]: {
    message: (
      <Text>
        Please{" "}
        <TextLink href="#">request pre-installation with Opticomm</TextLink> for
        this address.
      </Text>
    ),
    tone: "info",
    title:
      "You can't use this form to request an internet connection right now",
  },

  [InternetRequestStatusEnum.PRE_INSTALL_NBN]: {
    message: (
      <Text>
        Please{" "}
        <TextLink href="https://www.nbnco.com.au/develop-or-plan-with-the-nbn/new-developments/pre-installation-request/new">
          request pre-installation with NBN
        </TextLink>{" "}
        for this address.
      </Text>
    ),
    tone: "info",
    title:
      "You can't use this form to request an internet connection right now",
  },
  [InternetRequestStatusEnum.ADDRESS_NOT_FOUND]: {
    message: (
      <Text>
        You will not be able to request internet at this time and you will need
        to try again later as we cant offer a retail plan for the address at
        this time.
      </Text>
    ),
    tone: "caution",
    title: "We couldn't find your address for an Internet connection",
  },
};

const ServiceTypeStatusAlert: React.FC<{
  status: InternetRequestStatusEnum | null;
}> = ({ status }) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const {
    context: { featureFlags },
  } = useAppContext();
  const baseConfig =
    INTERNET_CONN_STATUS_ALERTS[status as InternetRequestStatusEnum];

  if (!baseConfig) return null;
  if (
    featureFlags.enablePreinstallAddrNotFound &&
    status === InternetRequestStatusEnum.PRE_INSTALL_NBN
  )
    return null;

  const config =
    featureFlags.enablePreinstallAddrNotFound &&
    status === InternetRequestStatusEnum.ADDRESS_NOT_FOUND
      ? {
          ...baseConfig,
          message: (
            <Text>
              You can still request Internet, but we'll need to check your
              address manually. We'll be in touch if there is any issues.
            </Text>
          ),
        }
      : baseConfig;

  const handleInternetDeselect = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    setFieldValue(
      "selectedConnections",
      values.selectedConnections.filter(
        (connection) => connection !== "Internet"
      )
    );
    setFieldValue("internetRequestStatus", null);
  };

  return (
    <Alert title={config.title} tone={config.tone}>
      {config.message}
      {!featureFlags.enablePreinstallAddrNotFound &&
        (status === InternetRequestStatusEnum.ADDRESS_NOT_FOUND ||
          status === InternetRequestStatusEnum.PRE_INSTALL_NBN) && (
          <>
            <br />
            <Text>
              You can still proceed with requesting other services, but you must{" "}
              <TextLink href="#" onClick={handleInternetDeselect}>
                deselect Internet
              </TextLink>{" "}
              before you can create the premise.
            </Text>
          </>
        )}
    </Alert>
  );
};

export default ServiceTypeStatusAlert;
